import {
  ActivateSettings,
  FinalizationRequest,
  Transaction,
  TransactionDetail,
  TransactionSearch,
  UploadTransactionFilesResponse,
} from "@/type/core/transaction/transaction.types";
import { DuplicateTransactionRequestDto } from "@state/transactions/request/duplicate.transaction.request.dto";
import { LaunchTransactionRequestDto } from "@state/transactions/request/launch.transaction.request.dto";
import { TransactionDetailResponseDto } from "@state/transactions/response/transaction.detail.response.dto";
import { ViewsSearchResponseDto } from "@state/transactions/response/views.search.response.dto";
import { Pageable } from "@type/core/pagination/pagination.types";
import configuration from "@utils/Config";
import {
  restDeletionHandler,
  restDetailsHandler,
  restGetHandler,
  restListHandlerPagination,
  restPostHandler,
  restPostHandlerReceivingMultipart,
  restPostHandlerWithMultipleIds,
  restUpdateHandler,
  restUpdateMultipartHandler,
} from "@utils/rest/RestClient";
import { createEffect } from "effector";
import { TransactionSignatorySuccess } from "@state/transactions/request/TransactionSignatorySuccess";
import { MultipleTransactionActionResponse } from "@state/transactions/response/MultipleTransactionActionResponse";
import { TransactionsWaitingForSignature } from "@state/transactions/response/TransactionsWaitingForSignature";
import { ErrorResponse } from "@utils/rest/ServerResponseParse";
import { WS_UPLOAD_TIMEOUT } from "@/utils/Constant";

const baseUrl = configuration.backendBaseUrl;
const transactionUrl = baseUrl + "/ws/fo/transaction";
const viewsUrl = baseUrl + "/ws/fo/view";
const uploadFilesUrl = "/uploadFiles";
const uploadAttachmentsUrl = "/uploadAttachments";
const fileNumberUrl = "/fileNumber";
const transactionDetailSuffixUrl = "/detail/false/false/true";
const transactionDetailWithFileDataSuffixUrl = "/detail/false/true/false";
const transactionDetailLightSuffixUrl = "/detail/true/false/true";
const transactionLaunchSuffixUrl = "/process";
const transactionUpdateSuffixUrl = "/true";
const transactionDuplicateSuffixUrl = "/duplicate";
const transactionSettingsUrl = transactionUrl + "/settings";
const transactionConfidentialityUpdateSuffixUrl = "/confidentiality/true";
const transactionSignatorySuccessUrl =
  baseUrl + "/ws/fo/transaction/signatory/success";
const multipleTransactionUrl = baseUrl + "/ws/fo/transaction/transactions";
const reorderTransactionSignatoriesUrl = "/reorder";
const extendTransactionUrl = "/extend";
const finalizeTransactionUrl = "/finalize";
const exportDasbhoardUrl = transactionUrl + "/export";

export const requestSearchTransactions = createEffect({
  handler: restListHandlerPagination<
    Pageable<TransactionDetail>,
    TransactionSearch
  >(transactionUrl + "/search"),
});

export const requestSearchViews = createEffect({
  handler: restGetHandler<ViewsSearchResponseDto>(viewsUrl),
});

export const requestCreateTransaction = createEffect({
  handler: restPostHandler<Transaction, TransactionDetail>(transactionUrl),
});

export const requestUploadTransactionFiles = createEffect({
  handler: restUpdateMultipartHandler<UploadTransactionFilesResponse>(
    transactionUrl,
    uploadFilesUrl,
  ),
});

export const requestUploadTransactionAttachments = createEffect({
  handler: restUpdateMultipartHandler<void>(
    transactionUrl,
    uploadAttachmentsUrl,
  ),
});

export const requestGetFileNumber = createEffect({
  handler: restDetailsHandler<number>(transactionUrl, fileNumberUrl),
});

export const requestGetTransactionDetail = createEffect({
  handler: restDetailsHandler<TransactionDetailResponseDto>(
    transactionUrl,
    transactionDetailSuffixUrl,
  ),
});

export const requestGetTransactionDetailWithFileData = createEffect({
  handler: restDetailsHandler<TransactionDetailResponseDto>(
    transactionUrl,
    transactionDetailWithFileDataSuffixUrl,
  ),
});

export const requestGetTransactionDetailLight = createEffect({
  handler: restDetailsHandler<TransactionDetailResponseDto>(
    transactionUrl,
    transactionDetailLightSuffixUrl,
  ),
});

export const requestDeleteTransaction = createEffect({
  handler: restDeletionHandler<object>(transactionUrl),
});

export const requestLaunchTransaction = createEffect({
  handler: restPostHandler<LaunchTransactionRequestDto, void | ErrorResponse>(
    transactionUrl,
    transactionLaunchSuffixUrl,
    WS_UPLOAD_TIMEOUT,
  ),
});

export const requestUpdateTransaction = createEffect({
  handler: restUpdateHandler<Transaction, TransactionDetailResponseDto>(
    transactionUrl,
    transactionUpdateSuffixUrl,
  ),
});

export const requestDuplicateTransaction = createEffect({
  handler: restPostHandler<DuplicateTransactionRequestDto, TransactionDetail>(
    transactionUrl,
    transactionDuplicateSuffixUrl,
  ),
});

export const requestGetTransactionSettings = createEffect({
  handler: restGetHandler<ActivateSettings>(transactionSettingsUrl),
});

export const requestUpdateTransactionConfidentiality = createEffect({
  handler: restUpdateHandler<string[], void>(
    transactionUrl,
    transactionConfidentialityUpdateSuffixUrl,
  ),
});

export const requestGetTransactionSignatorySuccess = createEffect({
  handler: restGetHandler<
    TransactionsWaitingForSignature,
    TransactionSignatorySuccess
  >(transactionSignatorySuccessUrl),
});

export const requestPostMultipleTransaction = createEffect({
  handler: restPostHandler<string[], MultipleTransactionActionResponse>(
    multipleTransactionUrl,
  ),
});

export const requestPutReorderSignatories = createEffect({
  handler: restUpdateHandler<Transaction, void>(
    transactionUrl,
    reorderTransactionSignatoriesUrl,
  ),
});

export const requestPostExtendTransaction = createEffect({
  handler: restPostHandler<void, void>(transactionUrl, extendTransactionUrl),
});

export const requestPostExtendTransactionWithSignatory = createEffect({
  handler: restPostHandlerWithMultipleIds<void, void>(
    transactionUrl,
    extendTransactionUrl,
  ),
});

export const requestPostFinalizeTransaction = createEffect({
  handler: restPostHandler<FinalizationRequest, void>(
    transactionUrl,
    finalizeTransactionUrl,
  ),
});

export const requestPostExportDasbhoard = createEffect({
  handler: restPostHandlerReceivingMultipart<TransactionSearch, Blob>(
    exportDasbhoardUrl,
  ),
});
